import { Link } from 'core/i18n/navigation';
import { useTranslations } from 'next-intl';
import { appRoutes } from 'config/routing';

const AGENT_BADGE_KEYS = ['badge1', 'badge2', 'badge3'];

export const Section23 = () => {
  const t = useTranslations();
  return (
    <section className={styles.section}>
      <div className={styles.wrap}>
        <div className={styles.head}>
          <div className={styles.eyebrow}>
            {t('pages.about.products.eyebrow')}
          </div>
          <h2 className={styles.title}>
            {t.rich('pages.about.products.title', {
              highlight: (chunks) => (
                <span className={styles.highlight}>{chunks}</span>
              ),
            })}
          </h2>
          <p className={styles.lead}>{t('pages.about.products.lead')}</p>
        </div>

        <div className={styles.grid}>
          <div className={`${styles.card} ${styles.bgYellow}`}>
            {/* Light-only: dot grid fading in from the glow corner. */}
            <div className={styles.cardGrid} aria-hidden="true" />
            <span className={`${styles.tag} ${styles.accentYellow}`}>
              {t('pages.about.products.classic.tag')}
            </span>
            <h3 className={styles.cardTitle}>MindFeed Classic</h3>
            <p className={`${styles.line} ${styles.accentYellow}`}>
              {t('pages.about.products.classic.line')}
            </p>
            <p className={styles.body}>
              {t('pages.about.products.classic.body')}
            </p>
            <Link
              href={appRoutes.PRODUCT_MINDFEED_CLASSIC}
              className={styles.link}
            >
              {t('pages.about.products.classic.link')}{' '}
              <span aria-hidden="true">→</span>
            </Link>
          </div>

          <div className={`${styles.card} ${styles.bgBlue}`}>
            <div className={styles.cardGrid} aria-hidden="true" />
            <span className={`${styles.tag} ${styles.accentBlue}`}>
              {t('pages.about.products.business.tag')}
            </span>
            <h3 className={styles.cardTitle}>MindFeed Business</h3>
            <p className={`${styles.line} ${styles.accentBlue}`}>
              {t('pages.about.products.business.line')}
            </p>
            <p className={styles.body}>
              {t('pages.about.products.business.body')}
            </p>
            <span className={styles.soon}>
              {t('pages.about.products.business.soon')}
            </span>
          </div>

          <div className={`${styles.cardWide} ${styles.bgGreen}`}>
            <div className={styles.cardGrid} aria-hidden="true" />
            <div className={styles.content}>
              <span className={`${styles.tag} ${styles.accentGreen}`}>
                {t('pages.about.products.agents.tag')}
              </span>
              <h3 className={styles.cardTitle}>
                {t('pages.about.products.agents.title')}
              </h3>
              <p className={`${styles.line} ${styles.accentGreen}`}>
                {t('pages.about.products.agents.line')}
              </p>
              <p className={styles.body}>
                {t('pages.about.products.agents.body')}
              </p>
            </div>
            <div className={`${styles.badges} ${styles.content}`}>
              {AGENT_BADGE_KEYS.map((badgeKey) => (
                <span key={badgeKey} className={styles.badge}>
                  {t(`pages.about.products.agents.${badgeKey}`)}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const CARD_BORDER = ['border-[#F0F0F0] marketing-dark:border-white/10'].join(
  ' ',
);

const styles = {
  section: 'marketing-dark:bg-mf-navy pt-[74px] pb-5 text-center',
  wrap: 'mx-auto max-w-[1200px] px-6 lg:px-10',
  head: 'mx-auto max-w-[720px]',
  eyebrow:
    'mb-4 text-[13px] font-semibold uppercase tracking-[0.15em] text-mf-blue marketing-dark:text-mf-coral',
  title:
    'font-inter text-3xl font-extrabold leading-[1.1] tracking-[-0.02em] text-mf-off-black-600 marketing-dark:text-white sm:text-[40px]',
  highlight: 'text-mf-blue marketing-dark:text-mf-coral',
  lead: 'mx-auto mt-[18px] max-w-[560px] text-lg leading-[1.65] text-mf-gray-600 marketing-dark:text-white/66',
  grid: 'mt-[46px] grid grid-cols-1 gap-[22px] text-left lg:grid-cols-2',
  card: `relative flex flex-col items-start overflow-hidden rounded-[22px] border p-[34px] ${CARD_BORDER}`,
  cardWide: `relative grid grid-cols-1 items-center gap-8 overflow-hidden rounded-[22px] border p-[34px] text-left lg:col-span-2 lg:grid-cols-[1.4fr_1fr] ${CARD_BORDER}`,
  cardGrid: [
    'pointer-events-none absolute inset-0 marketing-dark:hidden',
    'bg-[radial-gradient(circle,#DADADA_1px,transparent_1.4px)] bg-[length:28px_28px]',
    '[mask-image:radial-gradient(70%_90%_at_88%_10%,#000_0%,transparent_70%)]',
  ].join(' '),
  content: 'relative z-[2]',
  // Accent-tinted card backgrounds: white base in light, navy gradient in dark.
  // Card backgrounds: white with a soft accent corner glow in light (the
  // band-card treatment); accent-tinted navy gradients in dark.
  bgYellow: [
    'bg-white bg-[image:radial-gradient(46%_60%_at_92%_6%,rgba(247,203,45,0.22)_0%,transparent_62%)]',
    'marketing-dark:bg-[image:radial-gradient(120%_120%_at_90%_0%,rgba(247,203,45,0.16)_0%,transparent_55%),linear-gradient(160deg,#13234f,#0c1838)]',
  ].join(' '),
  bgBlue: [
    'bg-white bg-[image:radial-gradient(46%_60%_at_92%_6%,rgba(189,222,255,0.5)_0%,transparent_62%)]',
    'marketing-dark:bg-[image:radial-gradient(120%_120%_at_90%_0%,rgba(91,141,255,0.24)_0%,transparent_55%),linear-gradient(160deg,#13234f,#0c1838)]',
  ].join(' '),
  bgGreen: [
    'bg-white bg-[image:radial-gradient(46%_60%_at_92%_6%,rgba(110,215,166,0.28)_0%,transparent_62%)]',
    'marketing-dark:bg-[image:radial-gradient(110%_130%_at_88%_0%,rgba(31,122,82,0.30)_0%,transparent_58%),linear-gradient(160deg,#0f2545,#0c1838)]',
  ].join(' '),
  // Accent text follows each card's colour: readable mid-tones on white,
  // pastels on navy.
  accentYellow: 'text-[#B18E1E] marketing-dark:text-[#FDE47A]',
  accentBlue: 'text-mf-blue marketing-dark:text-[#7fa8ff]',
  accentGreen: 'text-[#2BA471] marketing-dark:text-[#6ed7a6]',
  tag: 'relative z-[2] mb-3.5 text-[12px] font-semibold uppercase tracking-[0.12em]',
  cardTitle:
    'relative z-[2] font-inter text-[25px] font-extrabold text-mf-off-black-600 marketing-dark:text-white',
  line: 'relative z-[2] mt-1.5 text-[15px] font-semibold',
  body: 'relative z-[2] mt-3 text-[15px] leading-[1.65] text-mf-gray-600 marketing-dark:text-white/62',
  link: [
    'relative z-[2] mt-5 inline-flex items-center gap-1.5 text-[15px] font-semibold transition-colors',
    'text-mf-off-black-600 hover:text-mf-blue',
    'marketing-dark:text-white marketing-dark:hover:text-mf-coral',
  ].join(' '),
  soon: [
    'relative z-[2] mt-5 inline-flex items-center rounded-full border px-3 py-1 text-[12px] font-semibold',
    'border-[#DADADA] bg-white text-mf-gray-500',
    'marketing-dark:border-white/15 marketing-dark:bg-white/[0.06] marketing-dark:text-white/55',
  ].join(' '),
  badges: 'flex flex-wrap gap-2.5',
  badge: [
    'inline-flex items-center rounded-full border px-3.5 py-2 text-[13px] font-semibold',
    'border-[#2BA471]/30 bg-[#2BA471]/10 text-[#1f6b46]',
    'marketing-dark:border-[#6ed7a6]/35 marketing-dark:bg-[#6ed7a6]/10 marketing-dark:text-[#9be7c4]',
  ].join(' '),
};
